import React, { useCallback, useMemo, useState } from 'react';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import formatCurrency, { sumKronor } from './Formatting';
import CostCenterItemSelect from './CostCenterItemSelect';
import ProjectSelect from './ProjectSelect';
import SelectInput from './SelectInput';
import { scopedTree } from './Scope';
import { Account, CostCenterItem, EntryAmount, InvoiceInfo, InvoiceSplit, ProjectWithIncomeAccounts } from './Types';


// Where income books: kostnadsställe, projekt and konto. A part of the invoices with no place of its
// own books on the default place, the one picked above the list.
export type IncomePlace = { costCenterItemId: string | null, projectId: string | null, incomeAccount: number | null }

export const isCompletePlace = (place: IncomePlace) =>
    place.costCenterItemId !== null && place.projectId !== null && place.incomeAccount !== null

export const UNSPLIT = 'unsplit'

const accountValueGetter = (account: Account) => account.account_number
const accountNameGetter = (account: Account) => account.account_name

function getCostCenterItem<P>(costCenterItems: CostCenterItem<P>[], costCenterItemId: string | null) {
    return costCenterItems.find(item => item.cost_center_item_id === costCenterItemId) ?? null
}

export const ThreeSelector = ({ costCenterItems, officialIncomeAccounts, costCenterItemId, projectId, incomeAccount, setCostCenterItemId, setProjectId, setIncomeAccount }:
    {
        costCenterItems: CostCenterItem<ProjectWithIncomeAccounts>[],
        officialIncomeAccounts: Account[],
        costCenterItemId: string | null,
        projectId: string | null,
        incomeAccount: number | null,
        setCostCenterItemId: (newValue: string) => void,
        setProjectId: (newValue: string | null) => void,
        setIncomeAccount: (newValue: number | null) => void
    }
) => {
    const changeCostCenterItemHandler = useCallback(
        (newCostCenterItemId: string) => { setCostCenterItemId(newCostCenterItemId); setProjectId(null); setIncomeAccount(null) },
        [setCostCenterItemId, setProjectId, setIncomeAccount]
    )
    const changeProjectHandler = useCallback(
        (newProjectId: string) => { setProjectId(newProjectId); setIncomeAccount(null) },
        [setProjectId, setIncomeAccount]
    )
    const accountOptions = useMemo<Account[]>(() => {
        const project = getCostCenterItem(costCenterItems, costCenterItemId)?.projects.find(project => project.project_id === projectId)
        return officialIncomeAccounts.filter(account => project?.income_account_numbers.includes(account.account_number) ?? false)
    }, [costCenterItems, officialIncomeAccounts, costCenterItemId, projectId])

    return <>
        <CostCenterItemSelect required={true} label='Kostnadsställe' costCenterItems={costCenterItems} value={costCenterItemId} onChange={changeCostCenterItemHandler}/>
        <ProjectSelect disabled={!costCenterItemId} required={true} label='Projekt' costCenterItem={getCostCenterItem(costCenterItems, costCenterItemId)} value={projectId} onChange={changeProjectHandler}/>
        <SelectInput options={accountOptions} valueGetter={accountValueGetter} nameGetter={accountNameGetter} disabled={!projectId} required={true} label='Konto' value={incomeAccount} onChange={setIncomeAccount}/>
    </>
}

// Entry fees first, then whatever else the invoices hold by name, and what could not be split last —
// the order a reader checks them in. Mirrors part_order in invoice_split.py.
const partRank = (key: string) => key === 'ordinary' ? 0 : key === 'late' ? 1 : key === UNSPLIT ? 3 : 2
const comparePartKeys = (a: string, b: string) => partRank(a) - partRank(b) || a.localeCompare(b, 'sv')

const PlaceCell = ({ place, onEdit, onReset, onChange, costCenterItems, officialIncomeAccounts, scopeKeys }: {
    place: IncomePlace | undefined,
    onEdit: () => void,
    onReset: () => void,
    onChange: (patch: Partial<IncomePlace>) => void,
    costCenterItems: CostCenterItem<ProjectWithIncomeAccounts>[],
    officialIncomeAccounts: Account[],
    scopeKeys: ReadonlySet<string> | null,
}) => {
    const setCostCenterItemId = useCallback((costCenterItemId: string) => onChange({ costCenterItemId }), [onChange])
    const setProjectId = useCallback((projectId: string | null) => onChange({ projectId }), [onChange])
    const setIncomeAccount = useCallback((incomeAccount: number | null) => onChange({ incomeAccount }), [onChange])
    if (place === undefined) {
        return <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ color: '#666' }}>som ovan</span>
            <Button size="small" onClick={onEdit}>Ändra</Button>
        </div>
    }
    return <div style={{ display: 'flex', flexFlow: 'row wrap', alignItems: 'center', gap: '0.75rem' }}>
        <ThreeSelector
            costCenterItems={scopedTree(costCenterItems, scopeKeys, place.costCenterItemId, place.projectId)}
            officialIncomeAccounts={officialIncomeAccounts}
            costCenterItemId={place.costCenterItemId} projectId={place.projectId} incomeAccount={place.incomeAccount}
            setCostCenterItemId={setCostCenterItemId} setProjectId={setProjectId} setIncomeAccount={setIncomeAccount}/>
        <IconButton size="small" title="Boka som ovan" aria-label="Boka som ovan" onClick={onReset}>
            <ClearIcon fontSize="small"/>
        </IconButton>
    </div>
}

// One row per part of the invoices — ordinary entry fee, late fee, each kind of other line (rental
// cards, …) and what could not be split — with what it amounts to on the selected invoices and where
// it books. Every part books "som ovan" until it is given a place of its own.
export function SplitTable({ invoices, splits, selectedRefs, defaultPlace, places, setPlaces, costCenterItems, officialIncomeAccounts, scopeKeys }: {
    invoices: InvoiceInfo[],
    splits: Record<string, InvoiceSplit>,
    selectedRefs: string[],
    defaultPlace: IncomePlace,
    places: Record<string, IncomePlace>,
    setPlaces: React.Dispatch<React.SetStateAction<Record<string, IncomePlace>>>,
    costCenterItems: CostCenterItem<ProjectWithIncomeAccounts>[],
    officialIncomeAccounts: Account[],
    scopeKeys: ReadonlySet<string> | null,
}) {
    const [showUnsplit, setShowUnsplit] = useState(false)
    const [showAmounts, setShowAmounts] = useState(false)

    // The ordinary and late fee from the start — Eventor's fees say what they are, no invoice needs
    // reading — then every part any read invoice has, so the table does not jump as the selection
    // changes.
    const labels = useMemo(() => {
        const found = new Map<string, string>([['ordinary', 'Ordinarie anmälningsavgift'], ['late', 'Efteranmälningsavgift']])
        Object.values(splits).forEach(split => split.parts.forEach(part => found.set(part.key, part.label)))
        return found
    }, [splits])
    const keys = useMemo(() => [...labels.keys()].sort(comparePartKeys), [labels])

    // The selected invoices — or, before any is selected, every invoice read so far: the preview is
    // most useful before anything is chosen, and an empty selection would show only zeros.
    const noneSelected = selectedRefs.length === 0
    const scope = noneSelected ? Object.keys(splits) : selectedRefs
    const chosen = scope.map(ref => splits[ref]).filter((split): split is InvoiceSplit => split !== undefined)
    const partsOf = (key: string) => chosen.flatMap(split => split.parts.filter(part => part.key === key))
    const total = sumKronor(chosen.flatMap(split => split.parts.map(part => part.amount)))
    // Checked against the invoices that have been read: the unread ones are taken apart at import.
    const invoicesTotal = sumKronor(invoices.filter(invoice => splits[String(invoice.invoice_number)] !== undefined
                                                               && scope.includes(String(invoice.invoice_number)))
                                            .map(invoice => invoice.total_amount))
    const unreadSelected = scope.length - chosen.length
    const byNumber = new Map(invoices.map(invoice => [String(invoice.invoice_number), invoice]))
    const unsplitLines = chosen.flatMap(split => split.unsplit_lines.map(line =>
        ({ ...line, invoice: byNumber.get(String(split.invoice_number)) })))

    // The entry lines by amount and how each was split, over the same invoices as the table: the
    // quickest check that the rules are right — every 270 in an adult class 180 + 90, say.
    const entryAmounts = useMemo(() => {
        const counted = new Map<string, EntryAmount>()
        chosen.forEach(split => split.entry_amounts.forEach(row => {
            const key = `${row.amount}|${row.ordinary}|${row.late}|${row.unsplit}`
            const seen = counted.get(key)
            counted.set(key, seen === undefined ? { ...row } : { ...seen, lines: seen.lines + row.lines })
        }))
        return [...counted.values()].sort((a, b) => a.amount - b.amount || b.ordinary - a.ordinary)
    }, [chosen])

    const edit = (key: string) => setPlaces(p => ({ ...p, [key]: { ...defaultPlace } }))
    const reset = (key: string) => setPlaces(p => { const { [key]: _, ...rest } = p; return rest })
    const change = (key: string, patch: Partial<IncomePlace>) => setPlaces(p => ({ ...p, [key]: { ...p[key], ...patch } }))

    return (<>
        <TableContainer component={Paper} variant="outlined" sx={{ width: 'fit-content', maxWidth: '100%' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Del</TableCell>
                        <TableCell align="right">Rader</TableCell>
                        <TableCell align="right">Belopp</TableCell>
                        <TableCell>Bokförs på</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {keys.map(key => {
                        const parts = partsOf(key)
                        const unsplit = key === UNSPLIT
                        return <React.Fragment key={key}>
                            <TableRow sx={unsplit && parts.length > 0 ? { '& td': { color: '#b26a00' } } : undefined}>
                                <TableCell>
                                    {unsplit ? '⚠ ' : ''}{labels.get(key)}
                                    {unsplit && unsplitLines.length > 0 &&
                                        <Button size="small" sx={{ ml: 1 }} onClick={() => setShowUnsplit(s => !s)}>
                                            {showUnsplit ? 'Dölj rader' : 'Visa rader'}
                                        </Button>}
                                </TableCell>
                                <TableCell align="right">{parts.reduce((n, part) => n + part.lines, 0)}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(sumKronor(parts.map(part => part.amount)))}</TableCell>
                                <TableCell>
                                    <PlaceCell
                                        place={places[key]}
                                        onEdit={() => edit(key)}
                                        onReset={() => reset(key)}
                                        onChange={patch => change(key, patch)}
                                        costCenterItems={costCenterItems} officialIncomeAccounts={officialIncomeAccounts} scopeKeys={scopeKeys}/>
                                </TableCell>
                            </TableRow>
                            {unsplit && showUnsplit &&
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ py: 0 }}>
                                        <Table size="small">
                                            <TableBody>
                                                {unsplitLines.map((line, index) =>
                                                    <TableRow key={index}>
                                                        <TableCell>{line.invoice?.org_name ?? ''}</TableCell>
                                                        <TableCell>{line.invoice?.invoice_number ?? ''}</TableCell>
                                                        <TableCell>{line.text}</TableCell>
                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(line.amount)}</TableCell>
                                                    </TableRow>)}
                                            </TableBody>
                                        </Table>
                                    </TableCell>
                                </TableRow>}
                        </React.Fragment>
                    })}
                    <TableRow sx={{ '& td': { fontWeight: 600 } }}>
                        <TableCell>Summa</TableCell>
                        <TableCell align="right">{chosen.reduce((n, split) => n + split.lines, 0)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(total)}</TableCell>
                        <TableCell sx={{ fontWeight: 'normal !important', color: '#666' }}>
                            {total !== invoicesTotal
                                ? <span style={{ color: '#c62828' }}>Fakturornas belopp är {formatCurrency(invoicesTotal)}</span>
                                : noneSelected
                                ? `alla ${chosen.length} inlästa ${chosen.length === 1 ? 'faktura' : 'fakturor'} — ingen vald än`
                                : unreadSelected > 0
                                ? `${chosen.length} av ${selectedRefs.length} valda fakturor inlästa`
                                : `${chosen.length} valda ${chosen.length === 1 ? 'faktura' : 'fakturor'}`}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
        {unreadSelected > 0 &&
            <span style={{ color: '#666', fontSize: '0.85em', maxWidth: '60rem' }}>
                Summorna gäller de inlästa fakturorna. Övriga delas upp när de importeras, och en del som bara
                finns i dem bokförs som ovan.
            </span>}
        {entryAmounts.length > 0 &&
            <Button size="small" onClick={() => setShowAmounts(s => !s)}>
                {showAmounts ? 'Dölj anmälningsraderna per belopp' : 'Visa anmälningsraderna per belopp'}
            </Button>}
        {showAmounts && entryAmounts.length > 0 &&
            <TableContainer component={Paper} variant="outlined" sx={{ width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" id="entry-amounts">
                    <TableHead>
                        <TableRow>
                            <TableCell align="right">Belopp</TableCell>
                            <TableCell align="right">Rader</TableCell>
                            <TableCell align="right">Ordinarie</TableCell>
                            <TableCell align="right">Efteranmälan</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entryAmounts.map(row =>
                            <TableRow key={`${row.amount}|${row.ordinary}|${row.late}|${row.unsplit}`}
                                      sx={row.unsplit ? { '& td': { color: '#b26a00' } } : undefined}>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(row.amount)}</TableCell>
                                <TableCell align="right">{row.lines}</TableCell>
                                {row.unsplit
                                    ? <TableCell colSpan={2} align="right">⚠ kunde inte delas upp</TableCell>
                                    : <>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(row.ordinary)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.late === 0 ? '–' : formatCurrency(row.late)}</TableCell>
                                      </>}
                            </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>}
    </>)
}
